// UI context — UI / selection state, kept OUT of the map context (US-874 · R-9).
//
// Selection and view state the shell's regions (and the feature US that fill
// them) read and write: the active rail view (which tab's content the left panel
// shows), the selected country, the selection granularity and the draw-mode
// toggle. Separated from MapContext so map re-renders and UI re-renders stay
// independent — no single "god context". Consume it through the `useUI` hook.
//
// US-878 models `selectedCountry` as a DRILL PATH and adds the pure navigation
// actions (selectFeature / drillUp / clearSelection) that move along it. The
// state machine lives here (R-8); applying it to the map lives in
// SelectionContext. Shape:
//   null
//   | { level: "country",   iso3, name }
//   | { level: "provinces", iso3, name, admin1: { id, name } }
//   | { level: "districts", iso3, name, admin1: {…}, admin2: { id, name } }
import { createContext, useCallback, useMemo, useState } from "react";

// Rail views (mockup tabs). Mockup default is "analysis"; we default to
// "map-config" for now so the panel shows working layer controls until the
// Analysis view (US-879/880/881) is built — flip the default below to restore
// the mockup's landing view once Analysis has content.
export const RAIL_VIEWS = ["analysis", "map-config", "definitions", "about"];
export const DEFAULT_VIEW = "map-config";

export const UIContext = createContext(null);

// Build a normalized drill-path node from a derived feature payload. Deeper
// levels carry the shallower admin units so drill-up can peel them off. Pure.
const buildSelection = (level, payload) => {
  if (!payload || !payload.iso3) return null;
  const { iso3, name, admin1 = null, admin2 = null } = payload;
  const base = { level, iso3, name: name ?? iso3 };
  if (level === "provinces") return { ...base, admin1 };
  if (level === "districts") return { ...base, admin1, admin2 };
  return base; // country
};

export const UIProvider = ({ children }) => {
  const [activeView, setActiveView] = useState(DEFAULT_VIEW); // active rail tab
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectionLevel, setSelectionLevel] = useState("country"); // selection granularity
  const [drawMode, setDrawMode] = useState(false);
  // Mobile-only: whether the rail/panel slide-over drawer is open. Desktop keeps
  // the rail + panel always in the grid, so this is ignored there.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // First-run wayfinding (US-877 · R-1/R-2): the landing guide shows until the
  // analyst interacts (makes a selection, or explicitly dismisses it) and returns
  // only on reset to home (Clear / drill-up to APAC).
  const [hasInteracted, setHasInteracted] = useState(false);

  // Select a unit derived from a map feature: set the drill path AND align the
  // active level to it. `payload` is the already-normalized { iso3, name,
  // admin1?, admin2? } — feature parsing stays in SelectionContext (R-8).
  const selectFeature = useCallback((level, payload) => {
    const next = buildSelection(level, payload);
    if (!next) return;
    setSelectedCountry(next);
    setSelectionLevel(level);
    setHasInteracted(true); // any selection (map or dropdown) dismisses the guide
  }, []);

  // Drill back up to a shallower level, peeling off deeper admin units. A null /
  // "apac" target clears the selection back to the APAC root.
  const drillUp = useCallback((toLevel) => {
    if (!toLevel || toLevel === "apac") {
      setSelectedCountry(null);
      setSelectionLevel("country");
      setHasInteracted(false); // reset to home re-shows the landing guide (R-2)
      return;
    }
    setSelectedCountry((prev) => {
      if (!prev) return prev;
      if (toLevel === "country") {
        return { level: "country", iso3: prev.iso3, name: prev.name };
      }
      if (toLevel === "provinces") {
        return {
          level: "provinces",
          iso3: prev.iso3,
          name: prev.name,
          admin1: prev.admin1 ?? null,
        };
      }
      return prev;
    });
    setSelectionLevel(toLevel);
  }, []);

  // Clear the selection: back to the APAC view at country granularity.
  const clearSelection = useCallback(() => {
    setSelectedCountry(null);
    setSelectionLevel("country");
    setHasInteracted(false); // reset to home re-shows the landing guide (R-2)
  }, []);

  // Mark that the analyst has engaged (used by the landing guide's dismiss
  // button, US-877 · R-2). A selection sets this implicitly via selectFeature.
  const markInteracted = useCallback(() => setHasInteracted(true), []);

  const value = useMemo(
    () => ({
      activeView,
      setActiveView,
      selectedCountry,
      setSelectedCountry,
      selectionLevel,
      setSelectionLevel,
      selectFeature,
      drillUp,
      clearSelection,
      hasInteracted,
      markInteracted,
      drawMode,
      setDrawMode,
      mobileNavOpen,
      setMobileNavOpen,
    }),
    [
      activeView,
      selectedCountry,
      selectionLevel,
      selectFeature,
      drillUp,
      clearSelection,
      hasInteracted,
      markInteracted,
      drawMode,
      mobileNavOpen,
    ]
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};
